import { ERRS } from 'core'
import type { ErrorObject } from 'serialize-error'
import type { FormFactor } from '##schema/enums.ts'
import { MAIN_HOST } from '#constants.ts'
import { endSlashReg } from '#utils/regs.ts'

export function getSiteUrl({ domain, path }: { domain: string; path: string }) {
  // On root urls `url.path` is just a slash, it needs to be remove to avoid a
  // double slash
  return `${domain}/${path === '/' ? '' : path}`
}

export function getCanonical(matches: any) {
  const match = matches.at(-1)
  if (!match) return {}

  return {
    // Replace slashes in the end
    href: (MAIN_HOST + match?.pathname).replace(endSlashReg, ''),
    rel: 'canonical',
    tagName: 'link',
  }
}

export type Rounding = 't' | 'r' | 'b' | 'l' | 'all' | 'none'
export type Size = 'sm' | 'md' | 'lg' | 'xl'

const roundingClasses = {
  b: 'rounded-b',
  l: 'rounded-l',
  r: 'rounded-r',
  t: 'rounded-t',
  all: 'rounded',
  none: '',
}
const roundingClassesMd = {
  b: 'rounded-b-md',
  l: 'rounded-l-md',
  r: 'rounded-r-md',
  t: 'rounded-t-md',
  all: 'rounded-md',
  none: '',
}
const sizeClasses = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg',
}

export function getRounding(size: Size, rounding: Rounding) {
  if (rounding === 'none') return ''
  if (size === 'lg' || size === 'xl') return roundingClassesMd[rounding] + ' '

  return roundingClasses[rounding] + ' '
}

/**
 * Get’s the size class for an button including paddings
 */
export function getSize(size: Size, rounding: Rounding, isSquare = false) {
  const r = getRounding(size, rounding)
  if (isSquare) {
    const squareSizeClasses = {
      sm: r + 'p-0.5 text-xs',
      md: r + 'p-1.5 text-xs',
      lg: r + 'p-2 text-sm',
      xl: r + 'p-2.5 text-base',
    }

    return squareSizeClasses[size]
  }

  const sizeClasses = {
    // Not entirely sure why the sm size needs the padding tweaks
    sm: r + 'px-2 pt-[0.1rem] pb-[0.14rem] text-xs',
    md: r + 'px-2.5 py-1.5 text-xs',
    lg: r + 'px-2.5 py-1.5 text-sm',
    xl: r + 'px-2.5 py-1.5 text-base',
  }

  return sizeClasses[size]
}

export function getLinkSize(size: Size) {
  return sizeClasses[size]
}

export const sleep = (ms = 500) =>
  new Promise(resolve => setTimeout(resolve, ms))

/**
 * Merge multiple headers objects into one (uses set so headers are overridden)
 */
export function mergeHeaders(
  ...headers: (ResponseInit['headers'] | null | undefined)[]
) {
  const merged = new Headers()
  for (const header of headers) {
    if (!header) continue
    for (const [key, value] of new Headers(header).entries()) {
      merged.set(key, value)
    }
  }

  return merged
}

/**
 * Combine multiple header objects into one (uses append so headers are not
 * overridden)
 */
export function combineHeaders(
  ...headers: (ResponseInit['headers'] | null | undefined)[]
) {
  const combined = new Headers()
  for (const header of headers) {
    if (!header) continue
    for (const [key, value] of new Headers(header).entries()) {
      combined.append(key, value)
    }
  }

  return combined
}

/**
 * Combine multiple response init objects into one (uses combineHeaders)
 */
export function combineResponseInits(
  ...responseInits: (ResponseInit | null | undefined)[]
) {
  let combined: ResponseInit = {}

  for (const responseInit of responseInits) {
    combined = {
      ...responseInit,
      headers: combineHeaders(combined.headers, responseInit?.headers),
    }
  }

  return combined
}

export function getDomainUrl(request: Request) {
  const host =
    request.headers.get('X-Forwarded-Host') ??
    request.headers.get('host') ??
    new URL(request.url).host
  const protocol = host.includes('localhost') ? 'http' : 'https'
  return `${protocol}://${host}`
}

export function flipFormFactor(formFactor: FormFactor): FormFactor {
  return formFactor === 'mobile' ? 'desktop' : 'mobile'
}

// Narrows types index signature
export function hasKey<O extends object>(
  obj: O,
  key: PropertyKey
): key is keyof O {
  return key in obj
}

export function getErrMsg(err: Error | ErrorObject): string {
  if (err instanceof Error) return err.message
  if (err && 'message' in err && err.message) return err.message

  return ERRS.mystery
}

function calc(m: number) {
  return (n: number) => Math.round(n * m)
}

// A TS rip of https://www.npmjs.com/package/milliseconds
export const ms = {
  seconds: calc(1e3),
  minutes: calc(6e4),
  hours: calc(36e5),
  days: calc(864e5),
  weeks: calc(6048e5),
  months: calc(26298e5),
  years: calc(315576e5),
}

export function getRandomNum(min: number, max: number) {
  return Number.parseInt((Math.random() * (max - min) + min).toFixed(0), 10)
}

// https://github.com/GoogleChrome/lighthouse/blob/192e509e6f2990696e71fe544dfc546b1de5b251/report/renderer/i18n-formatter.js#L30
export function parseGranularity(granularity: number | undefined) {
  if (typeof granularity === 'undefined') return 0
  const log10 = -Math.log10(granularity)

  if (!Number.isInteger(log10)) {
    console.warn(`granularity of ${granularity} is invalid. Using 1 instead`)
    return 1
  }

  return log10
}
